import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, AlertTriangle, Send, Check, Loader2, Radio, RefreshCw, Activity, MapPin, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";

// Some backend routes wrap 4xx `detail` in a structured object
// (e.g. { error, message }). Toast can only render strings, so normalise.
function toastMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || d.error || fallback;
  return err?.message || fallback;
}

/**
 * BLOCK 38 ,  Admin Platform Ops
 *
 * Two admin-only surfaces the orphan-route lint (BLOCK 37) surfaced as
 * unreachable from the SPA:
 *
 *   1. Platform Email Provider picker ,  flips whether Acquigine's own
 *      platform emails (password reset, admin alerts, etc.) go through
 *      Brevo or Resend. Mirrors the subscriber-facing provider picker
 *      that already exists in IntegrationsPanel.
 *
 *   2. FL Wind-Down anomaly hold ,  the weekly wind-down digest job runs
 *      an anomaly check before sending; if this week's match count is
 *      significantly above the trailing average, the send is blocked and
 *      waits for admin approval. This panel exposes the anomaly status
 *      and the one-click "Approve and send anyway" override, plus a
 *      send-test path for a single recipient.
 */
export default function AdminPlatformOps() {
  return (
    <div className="space-y-6" data-testid="admin-platform-ops-page">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin</div>
        <h1 className="font-heading text-4xl tracking-tight">Platform Operations</h1>
        <p className="text-slate-400 text-sm mt-1">
          Platform-wide levers for email delivery and the FL wind-down weekly digest.
        </p>
      </header>

      <PlatformEmailProviderSection />
      <AbuseFlagsSection />
      <WebhookHealthSection />
      <ConnectorHealthSection />
      <FLWinddownAnomalySection />
    </div>
  );
}

// BLOCK 44 Item 1 ,  Connector Health card.
// Non-blocking silent-drift monitor. Same rigor as the Webhook Health
// card next to it: surface which discovery connectors have gone quiet
// without paging or blocking runs.
function ConnectorHealthSection() {
  const [health, setHealth] = useState(null);
  const [expanded, setExpanded] = useState({ red: true, amber: true, green: false, silent: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/connectors/health");
      setHealth(r.data);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to load connector health"));
    }
  };
  useEffect(() => { load(); }, []);

  const probeFdor = async () => {
    setBusy(true);
    try {
      const r = await api.post("/discovery/fdor-nal/probe-all");
      toast.success(`FDOR NAL: ${r.data.counties_available} counties available, ${r.data.connector_rows_updated} configs updated`);
      await load();
    } catch (e) {
      toast.error(toastMsg(e, "FDOR probe failed"));
    } finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    if (!health) return { red: [], amber: [], green: [], silent: [] };
    return health.rows.reduce((acc, r) => {
      (acc[r.bucket] = acc[r.bucket] || []).push(r);
      return acc;
    }, { red: [], amber: [], green: [], silent: [] });
  }, [health]);

  const bucketMeta = {
    red:    { label: "Red",    color: "text-red-400",     dot: "bg-red-500" },
    amber:  { label: "Amber",  color: "text-amber-400",   dot: "bg-amber-500" },
    green:  { label: "Green",  color: "text-emerald-400", dot: "bg-emerald-500" },
    silent: { label: "Silent", color: "text-slate-400",   dot: "bg-slate-500" },
  };

  return (
    <div className="ae-card p-6" data-testid="connector-health-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className={`h-4 w-4 ${
            (health?.counts?.red || 0) > 0 ? "text-red-400"
            : (health?.counts?.amber || 0) > 0 ? "text-amber-400"
            : "text-emerald-400"}`} />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            Discovery Connector Health
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={probeFdor}
            disabled={busy}
            data-testid="connector-health-fdor-probe-btn"
            className="text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Probe FDOR NAL (67 counties)
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="connector-health-refresh-btn"
            className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>
      {!health ? (
        <div className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Bucket header counts */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            {["red","amber","green","silent"].map(b => (
              <div key={b}
                   className="rounded-sm border border-white/10 bg-[#0A0D12] px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${bucketMeta[b].dot}`} />
                  {bucketMeta[b].label}
                </div>
                <div className={`font-mono tabular-nums text-2xl mt-1 ${bucketMeta[b].color}`}
                     data-testid={`connector-health-count-${b}`}>
                  {health.counts[b] || 0}
                </div>
              </div>
            ))}
          </div>

          {/* BLOCK 64 Item 1 ,  FDOR NAL geocoding coverage tile.
              Surfaces the census_geocode_run background job's real progress
              on the same card as every other connector's health. */}
          {health.geocoding && <GeocodingCoverageTile gc={health.geocoding} />}

          {/* Bucketed tables */}
          {["red","amber","green","silent"].map(b => {
            const list = grouped[b];
            if (!list.length) return null;
            const isOpen = expanded[b];
            return (
              <div key={b} className="mb-3" data-testid={`connector-health-bucket-${b}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(s => ({ ...s, [b]: !s[b] }))}
                  className="w-full flex items-center justify-between text-left px-3 py-2 rounded-sm border border-white/10 bg-[#0A0D12] hover:bg-[#0F131A]"
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2 w-2 rounded-full ${bucketMeta[b].dot}`} />
                    <span className={`text-sm font-medium ${bucketMeta[b].color}`}>
                      {bucketMeta[b].label}
                    </span>
                    <span className="text-xs text-slate-500">({list.length})</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">
                    {isOpen ? "hide" : "show"}
                  </span>
                </button>
                {isOpen && (
                  <div className="mt-2 border border-white/8 rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#0A0D12] text-[10px] uppercase tracking-widest text-slate-500">
                          <th className="text-left px-3 py-2">County</th>
                          <th className="text-left px-3 py-2">State</th>
                          <th className="text-left px-3 py-2">Category</th>
                          <th className="text-left px-3 py-2">Runner</th>
                          <th className="text-left px-3 py-2">Reason</th>
                          <th className="text-right px-3 py-2">Days idle</th>
                          <th className="text-right px-3 py-2">Rows last</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.slice(0, 25).map((r) => (
                          <tr key={r.cid} className="border-t border-white/5">
                            <td className="px-3 py-1.5">{r.county || ", "}</td>
                            <td className="px-3 py-1.5">{r.state || ", "}</td>
                            <td className="px-3 py-1.5">{r.category || ", "}</td>
                            <td className="px-3 py-1.5 text-slate-500">{r.source_type || ", "}</td>
                            <td className="px-3 py-1.5 text-slate-400">{r.reason}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{r.days_since_run ?? ", "}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{r.last_row_count ?? ", "}</td>
                          </tr>
                        ))}
                        {list.length > 25 && (
                          <tr>
                            <td colSpan="7" className="px-3 py-2 text-xs text-slate-500 text-center">
                              …and {list.length - 25} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// BLOCK 64 Item 1 ,  FDOR NAL Geocoding Coverage sub-card.
// Reads the `geocoding` block emitted by GET /api/admin/connectors/health
// and renders totals + coverage % + latest census_geocode_run status.
function GeocodingCoverageTile({ gc }) {
  const bucketMeta = {
    red:   { color: "text-red-400",     dot: "bg-red-500",     border: "border-red-500/40",     bg: "bg-red-500/[0.04]" },
    amber: { color: "text-amber-400",   dot: "bg-amber-500",   border: "border-amber-500/40",   bg: "bg-amber-500/[0.04]" },
    green: { color: "text-emerald-400", dot: "bg-emerald-500", border: "border-emerald-500/40", bg: "bg-emerald-500/[0.04]" },
  };
  const b = bucketMeta[gc.bucket] || bucketMeta.red;
  const run = gc.census_run || null;
  const runTs = run?.created_at
    ? fmtDateTime(run.created_at)
    : null;
  const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : ", ");

  return (
    <div className={`rounded-sm border ${b.border} ${b.bg} px-4 py-3 mb-5`}
         data-testid="connector-health-geocoding-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className={`h-4 w-4 ${b.color}`} />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            FDOR NAL Geocoding Coverage
          </div>
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest ${b.color}`}>
            <span className={`inline-block h-2 w-2 rounded-full ${b.dot}`} />
            {gc.bucket}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono"
             data-testid="geocoding-reason">
          {gc.reason || ", "}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">FDOR total</div>
          <div className="font-mono tabular-nums text-xl mt-1 text-slate-200"
               data-testid="geocoding-fdor-total">
            {fmt(gc.fdor_nal_total)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">With coords</div>
          <div className={`font-mono tabular-nums text-xl mt-1 ${b.color}`}
               data-testid="geocoding-with-coords">
            {fmt(gc.fdor_nal_with_coords)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Attempted</div>
          <div className="font-mono tabular-nums text-xl mt-1 text-slate-300"
               data-testid="geocoding-attempted">
            {fmt(gc.fdor_nal_attempted)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Coverage</div>
          <div className={`font-mono tabular-nums text-xl mt-1 ${b.color}`}
               data-testid="geocoding-coverage-pct">
            {typeof gc.coverage_pct === "number" ? `${gc.coverage_pct}%` : ", "}
          </div>
          {typeof gc.attempted_pct === "number" && (
            <div className="text-[10px] text-slate-500 mt-0.5">
              attempted: {gc.attempted_pct}%
            </div>
          )}
        </div>
      </div>
      {run ? (
        <div className="mt-3 pt-3 border-t border-white/8 text-xs"
             data-testid="geocoding-census-run">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
            Latest census_geocode_run
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className={`font-mono text-[11px] uppercase tracking-wider ${
              run.status === "success" ? "text-emerald-400"
              : run.status === "running" ? "text-amber-400"
              : run.status === "error" ? "text-red-400"
              : "text-slate-400"}`}
              data-testid="geocoding-run-status">
              {run.status || "unknown"}
            </span>
            {runTs && (
              <span className="text-slate-500 font-mono text-[11px]"
                    data-testid="geocoding-run-ts">
                {runTs}
              </span>
            )}
            {run.note && (
              <span className="text-slate-300 text-[11px] break-all"
                    data-testid="geocoding-run-note">
                {run.note}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-white/8 text-[11px] text-slate-500"
             data-testid="geocoding-census-run-empty">
          No census_geocode_run entry yet.
        </div>
      )}
    </div>
  );
}

// BLOCK 43 Item 4 ,  Webhook Health card.
// Surfaces the two signals that would have caught the 26-day Brevo
// `wrong_token` outage on day one:
//   1. Successful email_events in the last 24h (green when > 0, amber at 0).
//   2. wrong_token / no_token rejections in the last 24h (amber if > 0).
// Backed by the pre-existing GET /api/admin/webhooks/health endpoint.
function WebhookHealthSection() {
  const [health, setHealth] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/webhooks/health");
      setHealth(r.data);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to load webhook health"));
    }
  };
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm(
      "Rotate BREVO_WEBHOOK_TOKEN now?\n\n" +
      "A new token is generated and written to .env immediately. " +
      "You MUST paste it into the Brevo dashboard webhook (or via the Brevo API) " +
      "before Brevo's next event fires, or events will be rejected."
    )) return;
    setRotating(true);
    try {
      const r = await api.post("/admin/webhooks/rotate-brevo-token");
      setReveal(r.data.token);
      toast.success("Token rotated. Paste into Brevo dashboard now.");
      await load();
    } catch (e) {
      toast.error(toastMsg(e, "Rotation failed"));
    } finally {
      setRotating(false);
    }
  };

  const rejCount = health?.webhook_rejections_24h?.count ?? 0;
  const evtCount = health?.brevo_events_24h?.count ?? 0;
  const rejColor = rejCount > 0 ? "text-amber-400" : "text-emerald-400";
  const evtColor = evtCount > 0 ? "text-emerald-400" : "text-amber-400";

  return (
    <div className="ae-card p-6" data-testid="webhook-health-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className={`h-4 w-4 ${rejCount > 0 ? "text-amber-400" : "text-emerald-400"}`} />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            Webhook Health (last 24h)
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="webhook-health-refresh-btn"
          className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      {!health ? (
        <div className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-sm border border-white/10 bg-[#0A0D12] px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Brevo events received
              </div>
              <div className={`font-mono tabular-nums text-2xl mt-1 ${evtColor}`}
                   data-testid="webhook-health-events-24h">
                {evtCount}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Last: {health.brevo_events_24h.last_received
                  ? fmtDateTime(health.brevo_events_24h.last_received)
                  : "never"}
              </div>
            </div>
            <div className="rounded-sm border border-white/10 bg-[#0A0D12] px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Rejections (auth failure)
              </div>
              <div className={`font-mono tabular-nums text-2xl mt-1 ${rejColor}`}
                   data-testid="webhook-health-rejections-24h">
                {rejCount}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {health.webhook_rejections_24h.last_rejection
                  ? `Last: ${fmtDateTime(health.webhook_rejections_24h.last_rejection.created_at)} ,  ${health.webhook_rejections_24h.last_rejection.reason}`
                  : "None in window"}
              </div>
            </div>
            <div className="rounded-sm border border-white/10 bg-[#0A0D12] px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Brevo token
              </div>
              <div className="text-sm mt-1">
                {health.brevo_token.configured ? (
                  <span className="text-emerald-400">✓ configured</span>
                ) : (
                  <span className="text-amber-400">missing</span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Set: {health.brevo_token.set_at
                  ? fmtDateTime(health.brevo_token.set_at)
                  : "?"}
                {health.brevo_token.days_until_rotation != null &&
                  ` · rotate in ${health.brevo_token.days_until_rotation}d`}
              </div>
            </div>
          </div>
          {rejCount > 0 && evtCount === 0 && (
            <div className="mt-4 p-3 rounded-sm border border-amber-500/40 bg-amber-500/[0.06] text-xs text-amber-300">
              ⚠ Zero successful events + non-zero rejections = the deployed webhook token
              likely doesn&apos;t match the Brevo dashboard. Rotate + resync.
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={rotate}
              disabled={rotating}
              data-testid="webhook-rotate-brevo-btn"
              className="btn-ghost text-sm inline-flex items-center gap-2"
            >
              {rotating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Rotating…</>
              ) : (
                <><RefreshCw className="h-4 w-4" /> Rotate BREVO_WEBHOOK_TOKEN</>
              )}
            </button>
          </div>
          {reveal && (
            <div className="mt-3 p-3 rounded-sm border border-emerald-500/40 bg-emerald-500/[0.06]"
                 data-testid="webhook-rotate-reveal">
              <div className="text-[11px] text-emerald-300 mb-2">
                Copy this token into the Brevo dashboard webhook now. It will not be shown again.
              </div>
              <code className="block break-all font-mono text-xs bg-black/40 px-3 py-2 rounded-sm">
                {reveal}
              </code>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PlatformEmailProviderSection() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/platform-email-provider");
      setState(r.data);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to load provider"));
    }
  };
  useEffect(() => { load(); }, []);

  const setProvider = async (provider) => {
    if (!state || state.provider === provider) return;
    if (provider === "brevo" && !state.brevo_key_configured) {
      toast.error("BREVO_API_KEY is not set in the environment. Set it before switching.");
      return;
    }
    if (provider === "resend" && !state.resend_key_configured) {
      toast.error("RESEND_API_KEY is not set in the environment. Set it before switching.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch("/admin/platform-email-provider", { provider });
      toast.success(r.data?.note || "Provider switched");
      await load();
    } catch (e) {
      toast.error(toastMsg(e, "Failed to switch provider"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ae-card p-6" data-testid="platform-email-provider-panel">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="h-4 w-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Platform Email Provider</div>
      </div>
      {!state ? (
        <div className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-400 mb-4">
            Which service Acquigine&apos;s own platform email (password resets, admin
            alerts, transactional receipts) uses. Subscriber outbound still goes through
            each subscriber&apos;s own connected provider.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ProviderCard
              testId="platform-email-provider-brevo"
              name="Brevo"
              selected={state.provider === "brevo"}
              configured={state.brevo_key_configured}
              onClick={() => setProvider("brevo")}
              disabled={saving}
            />
            <ProviderCard
              testId="platform-email-provider-resend"
              name="Resend"
              selected={state.provider === "resend"}
              configured={state.resend_key_configured}
              onClick={() => setProvider("resend")}
              disabled={saving}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            Change takes effect immediately for this process and persists in
            <code className="mx-1 font-mono">system_settings.platform_email_provider</code> across
            restarts.
          </p>
        </>
      )}
    </div>
  );
}

function ProviderCard({ name, selected, configured, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || !configured}
      className={`text-left rounded-sm border px-4 py-3 transition-colors ${
        selected
          ? "border-emerald-500/60 bg-emerald-500/[0.06]"
          : "border-white/10 hover:border-white/20 bg-[#0A0D12]"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm">{name}</div>
        {selected && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400">
            <Check className="h-3 w-3" /> Active
          </span>
        )}
      </div>
      <div className={`text-xs mt-1 ${configured ? "text-slate-500" : "text-amber-400"}`}>
        {configured ? "API key configured" : "API key missing ,  set it before switching"}
      </div>
    </button>
  );
}

function FLWinddownAnomalySection() {
  const [status, setStatus] = useState(null);
  const [approving, setApproving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/digest/fl-winddown/anomaly-status");
      setStatus(r.data);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to load anomaly status"));
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async () => {
    if (!window.confirm(
      "Send the weekly FL wind-down digest to every eligible subscriber right now, " +
      "bypassing the anomaly hold? This action is logged in the audit trail."
    )) return;
    setApproving(true);
    try {
      const r = await api.post("/admin/digest/fl-winddown/approve-anomaly");
      toast.success("Digest sent ,  override recorded in audit log");
      await load();
      // Optional: surface the raw result object briefly for admin visibility.
      console.log("digest override result:", r.data);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to approve override"));
    } finally {
      setApproving(false);
    }
  };

  const sendTest = async () => {
    const payload = testEmail.trim() ? { email: testEmail.trim() } : {};
    setTesting(true);
    try {
      const r = await api.post("/admin/digest/fl-winddown/send-test", payload);
      const skipped = r.data?.skipped ? ` (skipped: ${r.data.skipped_reason || "no match"})` : "";
      toast.success(`Test digest processed${skipped}`);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to send test digest"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="ae-card p-6" data-testid="fl-winddown-anomaly-panel">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className={`h-4 w-4 ${status?.blocked ? "text-amber-400" : "text-emerald-400"}`} />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">
          FL Wind-Down Digest ,  Weekly Anomaly Hold
        </div>
      </div>
      {!status ? (
        <div className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatBox label="This week" value={status.this_week ?? ", "} />
            <StatBox label="Trailing avg" value={status.trailing_avg ?? ", "} />
            <StatBox label="Samples" value={status.samples ?? ", "} />
            <StatBox
              label="Multiplier"
              value={status.multiplier != null ? `${status.multiplier}×` : ", "}
            />
          </div>
          {status.blocked ? (
            <div className="ae-card p-4 border border-amber-500/30 bg-amber-500/[0.05]">
              <div className="text-sm text-amber-300 font-medium mb-2">
                🛑 Next weekly send is currently BLOCKED by the anomaly guard.
              </div>
              <p className="text-xs text-slate-400 mb-3">
                This week&apos;s match count is significantly above the trailing average.
                Review the counts, then approve the override if the spike is legitimate
                (e.g. new county coverage added).
              </p>
              <button
                onClick={approve}
                disabled={approving}
                data-testid="fl-winddown-approve-anomaly-btn"
                className="btn-primary text-sm inline-flex items-center gap-2"
              >
                {approving ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <><Check className="h-4 w-4" /> Approve override &amp; send anyway</>
                )}
              </button>
            </div>
          ) : (
            <div className="text-sm text-emerald-400 inline-flex items-center gap-2">
              <Check className="h-4 w-4" /> No anomaly detected. Next scheduled send will proceed normally.
            </div>
          )}
          <div className="border-t border-white/8 my-5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Send a test digest</div>
            <p className="text-xs text-slate-500 mb-3">
              Trigger an on-demand digest for a single user. Every rule the scheduled
              path applies is respected (free-tier / opt-out / zero-match returns the skip reason).
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                data-testid="fl-winddown-test-email-input"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Leave blank to send to yourself"
                className="flex-1 min-w-[260px] bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm"
              />
              <button
                onClick={sendTest}
                disabled={testing}
                data-testid="fl-winddown-send-test-btn"
                className="btn-ghost text-sm inline-flex items-center gap-2"
              >
                {testing ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="h-4 w-4" /> Send test digest</>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-sm border border-white/10 bg-[#0A0D12] px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-lg mt-0.5">{value}</div>
    </div>
  );
}

// BLOCK 95 ,  Abuse Flags review surface.
// Reads velocity anomalies written by abuse.py (never auto-locks a user);
// lets an admin dismiss (false positive) or acknowledge (reviewed/actioned)
// each open flag. Backed by GET /admin/abuse-flags + POST .../resolve.
function AbuseFlagsSection() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("open");
  const [busyId, setBusyId] = useState(null);

  const load = async (status = filter) => {
    try {
      const r = await api.get(`/admin/abuse-flags?status=${status}`);
      setData(r.data);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to load abuse flags"));
    }
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const resolve = async (flag, action) => {
    const verb = action === "dismiss" ? "dismiss (false positive)" : "acknowledge (reviewed)";
    if (!window.confirm(`Mark this flag as ${verb} for ${flag.email}?`)) return;
    setBusyId(flag.id);
    try {
      await api.post(`/admin/abuse-flags/${flag.id}/resolve`, { action });
      toast.success(action === "dismiss" ? "Flag dismissed" : "Flag acknowledged");
      await load(filter);
    } catch (e) {
      toast.error(toastMsg(e, "Failed to resolve flag"));
    } finally {
      setBusyId(null);
    }
  };

  const flags = data?.flags || [];
  const openCount = data?.open_count ?? 0;

  const detailSummary = (f) => {
    const d = f.last_detail || {};
    const parts = [];
    if (d.per_hour != null) parts.push(`${d.per_hour}/hr`);
    if (d.per_day != null) parts.push(`${d.per_day}/day`);
    if (d.ops_today != null) parts.push(`${d.ops_today} ops today`);
    if (d.rows_today != null) parts.push(`${d.rows_today.toLocaleString()} rows today`);
    return parts.join(" · ") || ", ";
  };

  return (
    <div className="ae-card p-6" data-testid="abuse-flags-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className={`h-4 w-4 ${openCount > 0 ? "text-amber-400" : "text-emerald-400"}`} />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            Abuse Flags ,  Velocity Anomalies
          </div>
          {openCount > 0 && (
            <span
              data-testid="abuse-flags-open-count"
              className="px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-sm"
            >
              {openCount} open
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-sm border border-white/10 overflow-hidden text-[10px] uppercase tracking-widest">
            {["open", "resolved", "all"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                data-testid={`abuse-flags-filter-${s}`}
                className={`px-3 py-1.5 transition-colors ${
                  filter === s ? "bg-emerald-500/15 text-emerald-300" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load(filter)}
            data-testid="abuse-flags-refresh-btn"
            className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Velocity anomalies flagged for review. These never lock an account ,  they surface
        unusual pipeline-add or export activity so an admin can decide whether to act.
      </p>

      {!data ? (
        <div className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : flags.length === 0 ? (
        <div className="text-sm text-emerald-400 inline-flex items-center gap-2" data-testid="abuse-flags-empty">
          <Check className="h-4 w-4" /> No {filter === "all" ? "" : filter} flags. Nothing to review.
        </div>
      ) : (
        <div className="border border-white/8 rounded-sm overflow-hidden">
          <table className="w-full text-xs" data-testid="abuse-flags-table">
            <thead>
              <tr className="bg-[#0A0D12] text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-3 py-2">Account</th>
                <th className="text-left px-3 py-2">Threshold crossed</th>
                <th className="text-left px-3 py-2">Detail</th>
                <th className="text-right px-3 py-2">Hits</th>
                <th className="text-left px-3 py-2">Last seen</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.id} className="border-t border-white/5" data-testid={`abuse-flag-row-${f.id}`}>
                  <td className="px-3 py-2 text-slate-200">{f.email}</td>
                  <td className="px-3 py-2 text-amber-300">{f.kind_label}</td>
                  <td className="px-3 py-2 text-slate-400 font-mono">{detailSummary(f)}</td>
                  <td className="px-3 py-2 text-right font-mono">{f.hits}</td>
                  <td className="px-3 py-2 text-slate-400 font-mono">
                    {f.last_seen ? fmtDateTime(f.last_seen) : ", "}
                  </td>
                  <td className="px-3 py-2">
                    {f.status === "open" ? (
                      <span className="text-amber-400 uppercase text-[10px] tracking-wider">open</span>
                    ) : (
                      <span className="text-slate-500 uppercase text-[10px] tracking-wider">
                        {f.resolution || "resolved"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {f.status === "open" ? (
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          onClick={() => resolve(f, "acknowledge")}
                          disabled={busyId === f.id}
                          data-testid={`abuse-flag-acknowledge-${f.id}`}
                          className="btn-ghost text-[11px] px-2 py-1 disabled:opacity-50"
                        >
                          Acknowledge
                        </button>
                        <button
                          type="button"
                          onClick={() => resolve(f, "dismiss")}
                          disabled={busyId === f.id}
                          data-testid={`abuse-flag-dismiss-${f.id}`}
                          className="btn-ghost text-[11px] px-2 py-1 text-slate-400 disabled:opacity-50"
                        >
                          Dismiss
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-600">
                        {f.resolved_at ? fmtDateTime(f.resolved_at) : ""}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

